import React, { useEffect, useState } from 'react';
import { tryParseJson } from './utils';
import './styles.css';

/**
 * ComfortWidget — rich thermal-comfort display
 *
 * Expects data.response to be:
 *   { "<floorKey>": { air_temperature_c, mean_radiant_temperature_c,
 *                     operative_temperature_c, "predicted_mean_vote(pmv)",
 *                     "percentage_of_people_dissatisfied(ppd)",
 *                     thermal_comfort_score, solar_gain_w, conduction_gain_w,
 *                     ventilation_gain_w, hvac_power_w, internal_load_w,
 *                     annotation } }
 */

// ── Helpers ────────────────────────────────────────────────────────────────

const floorIcon = (name = '') => {
    const n = name.toLowerCase();
    if (n.includes('first')  || n === '1f') return '1️⃣';
    if (n.includes('second') || n === '2f') return '2️⃣';
    if (n.includes('third')  || n === '3f') return '3️⃣';
    if (n.includes('ground'))               return '🏢';
    if (n.includes('roof'))                 return '🔼';
    if (n.includes('basement'))             return '🔽';
    return '🏠';
};

const prettyKey = (k = '') =>
    k.replace(/_/g, ' ')
     .replace(/\(.*?\)/g, '')
     .trim()
     .replace(/\b\w/g, c => c.toUpperCase());

const scoreColor = (s) => s >= 70 ? '#4caf50' : s >= 50 ? '#ff9800' : '#f44336';

const pmvLabel = (v) => {
    if (v <= -2.5) return 'Cold';
    if (v <= -1.5) return 'Cool';
    if (v <= -0.5) return 'Slightly Cool';
    if (v <   0.5) return 'Neutral';
    if (v <   1.5) return 'Slightly Warm';
    if (v <   2.5) return 'Warm';
    return 'Hot';
};

const pmvPercent = (v) => ((Math.min(Math.max(v, -3), 3) + 3) / 6) * 100;

// ── Sub-components ─────────────────────────────────────────────────────────

const ScoreGauge = ({ score }) => {
    const r = 44;
    const circ = 2 * Math.PI * r;
    const pct = Math.min(Math.max(score, 0), 100) / 100;
    const color = scoreColor(score);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <svg width={110} height={110} viewBox="0 0 110 110">
                <circle cx={55} cy={55} r={r} fill="none" stroke="#e0e0e0" strokeWidth={10} />
                <circle
                    cx={55} cy={55} r={r} fill="none"
                    stroke={color} strokeWidth={10}
                    strokeDasharray={circ}
                    strokeDashoffset={circ * (1 - pct)}
                    strokeLinecap="round"
                    transform="rotate(-90 55 55)"
                />
                <text x={55} y={51} textAnchor="middle" fontSize={22} fontWeight={700} fill={color}>{score}</text>
                <text x={55} y={67} textAnchor="middle" fontSize={10} fill="#888">/100</text>
            </svg>
            <span style={{ fontSize: 12, color: '#555', fontWeight: 600 }}>Comfort Score</span>
        </div>
    );
};

const PMVScale = ({ pmv }) => {
    const pos = pmvPercent(pmv);
    const colors = ['#1565c0','#42a5f5','#81d4fa','#a5d6a7','#ffcc80','#ef9a9a','#c62828'];
    const labels = ['Cold','Cool','S.Cool','Neutral','S.Warm','Warm','Hot'];
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                PMV Scale
            </span>
            <div style={{ position: 'relative', height: 16, borderRadius: 8,
                background: `linear-gradient(to right, ${colors.join(',')})`,
                boxShadow: 'inset 0 1px 3px rgba(0,0,0,.2)' }}>
                <div style={{
                    position: 'absolute', top: -4, left: `${pos}%`,
                    transform: 'translateX(-50%)',
                    width: 4, height: 24, background: '#212121',
                    borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,.4)'
                }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {labels.map(l => <span key={l} style={{ fontSize: 9, color: '#888' }}>{l}</span>)}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                <span style={{ fontSize: 11, color: '#555' }}>PMV: <b style={{ color: '#1976d2' }}>{pmv}</b></span>
                <span style={{ fontSize: 11, color: '#555' }}>{pmvLabel(pmv)}</span>
            </div>
        </div>
    );
};

const TempCard = ({ label, value, icon, accent }) => (
    <div style={{
        flex: '1 1 0', minWidth: 80, background: '#fff',
        border: `2px solid ${accent}`, borderRadius: 10, padding: '12px 8px',
        textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,.07)'
    }}>
        <div style={{ fontSize: 22 }}>{icon}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: accent, marginTop: 4 }}>{value}°C</div>
        <div style={{ fontSize: 11, color: '#666', marginTop: 4, lineHeight: 1.3 }}>{label}</div>
    </div>
);

const GainBar = ({ label, value, maxAbs }) => {
    const pct = Math.min(Math.abs(value) / maxAbs, 1) * 100;
    const isNeg = value < 0;
    const color = isNeg ? '#1976d2' : '#f57c00';
    return (
        <div style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: '#444', fontWeight: 500 }}>{label}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color }}>
                    {isNeg ? '' : '+'}{value.toLocaleString(undefined, { maximumFractionDigits: 1 })} W
                </span>
            </div>
            <div style={{ height: 8, background: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4 }} />
            </div>
        </div>
    );
};

// ── Main component ─────────────────────────────────────────────────────────

const ComfortWidget = ({ data, onComplete }) => {
    const [zones, setZones] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (data && data.response) {
            try {
                const parsed = tryParseJson(data.response);
                if (parsed && typeof parsed === 'object') {
                    setZones(Object.entries(parsed));
                }
            } catch (e) {
                console.error('[ComfortWidget]', e);
            }
            setIsLoading(false);
            if (onComplete) onComplete('Comfort widget rendered');
        }
    }, [data]);

    if (isLoading) return <div className="archie-widget-loading">Loading comfort data…</div>;
    if (!zones.length) return <div className="kpi-empty-state"><p>No comfort data available</p></div>;

    return (
        <div style={{ fontFamily: '"Suisse Intl", sans-serif', padding: '16px 4px' }}>
            <h2 style={{ color: '#1a237e', marginBottom: 20, fontSize: 18, fontWeight: 700 }}>
                🌡️ Thermal Comfort Analysis
            </h2>

            {zones.map(([zoneName, m]) => {
                const score   = Number(m['thermal_comfort_score']) || 0;
                const pmv     = Number(m['predicted_mean_vote(pmv)']) || 0;
                const ppd     = Number(m['percentage_of_people_dissatisfied(ppd)']) || 0;
                const airT    = Number(m['air_temperature_c']) || 0;
                const mrt     = Number(m['mean_radiant_temperature_c']) || 0;
                const opT     = Number(m['operative_temperature_c']) || 0;
                const solar   = Number(m['solar_gain_w']) || 0;
                const cond    = Number(m['conduction_gain_w']) || 0;
                const vent    = Number(m['ventilation_gain_w']) || 0;
                const hvac    = Number(m['hvac_power_w']) || 0;
                const intLoad = Number(m['internal_load_w']) || 0;
                const annotation = m['annotation'] || '';
                const maxAbs = Math.max(...[solar, cond, vent, hvac, intLoad].map(Math.abs), 1);

                return (
                    <div key={zoneName} style={{
                        background: '#f5f7fa', border: '1px solid #dde3ef',
                        borderRadius: 14, padding: 20, marginBottom: 24,
                        boxShadow: '0 4px 16px rgba(0,0,0,.08)'
                    }}>
                        {/* Zone header */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                            <span style={{ fontSize: 24 }}>{floorIcon(zoneName)}</span>
                            <h3 style={{ margin: 0, color: '#1a237e', fontSize: 16, fontWeight: 700 }}>
                                {prettyKey(zoneName)}
                            </h3>
                        </div>

                        {/* Score + PMV row */}
                        <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                            <div style={{
                                background: '#fff', borderRadius: 12, padding: '16px 20px',
                                boxShadow: '0 2px 8px rgba(0,0,0,.07)',
                                display: 'flex', alignItems: 'center', gap: 20, flex: '0 0 auto'
                            }}>
                                <ScoreGauge score={score} />
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{
                                        fontSize: 28, fontWeight: 700,
                                        color: ppd > 30 ? '#f44336' : ppd > 15 ? '#ff9800' : '#4caf50'
                                    }}>{ppd}%</div>
                                    <div style={{ fontSize: 11, color: '#666', lineHeight: 1.4 }}>
                                        People<br/>Dissatisfied<br/>(PPD)
                                    </div>
                                </div>
                            </div>

                            <div style={{
                                background: '#fff', borderRadius: 12, padding: '16px 20px',
                                boxShadow: '0 2px 8px rgba(0,0,0,.07)', flex: '1 1 220px'
                            }}>
                                <PMVScale pmv={pmv} />
                            </div>
                        </div>

                        {/* Temperature cards */}
                        <div style={{ marginBottom: 20 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#555',
                                textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 10 }}>
                                🌡️ Temperatures
                            </div>
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                <TempCard label="Air Temperature"     value={airT} icon="💨" accent="#1976d2" />
                                <TempCard label="Mean Radiant Temp"   value={mrt}  icon="☀️" accent="#e65100" />
                                <TempCard label="Operative Temp"      value={opT}  icon="🏠" accent="#388e3c" />
                            </div>
                        </div>

                        {/* Heat gains */}
                        <div style={{
                            background: '#fff', borderRadius: 12, padding: '16px 20px',
                            boxShadow: '0 2px 8px rgba(0,0,0,.07)', marginBottom: 20
                        }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#555',
                                textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 14 }}>
                                📊 Heat Gains / Losses
                            </div>
                            <GainBar label="Solar Gain"       value={solar}   maxAbs={maxAbs} />
                            <GainBar label="Conduction Gain"  value={cond}    maxAbs={maxAbs} />
                            <GainBar label="Ventilation Gain" value={vent}    maxAbs={maxAbs} />
                            <GainBar label="HVAC Power"       value={hvac}    maxAbs={maxAbs} />
                            <GainBar label="Internal Load"    value={intLoad} maxAbs={maxAbs} />
                        </div>

                        {/* Annotation */}
                        {annotation && (
                            <div style={{
                                background: '#e8eaf6', border: '1px solid #9fa8da',
                                borderRadius: 10, padding: '12px 16px',
                                fontSize: 13, color: '#283593', lineHeight: 1.7
                            }}>
                                <span style={{ fontWeight: 700, marginRight: 6 }}>📝 Summary:</span>
                                {annotation}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default ComfortWidget;